import type { QuizQuestion, SectionRow } from '@/types'

import { useState } from 'react'
import { cn } from '@/lib/utils'
import { burstConfetti } from '@/lib/celebrate'

import {
    ArrowRight,
    CircleCheck,
    CircleX,
    RotateCcw,
    Trophy
} from 'lucide-react'

import SectionHeading from '@/components/landing/section-heading'

type OptionColumn = 'option_1' | 'option_2' | 'option_3' | 'option_4'

const COLUMNS: Array<{ number: number, column: OptionColumn, letter: string }> = [
    { number: 1, column: 'option_1', letter: 'A' },
    { number: 2, column: 'option_2', letter: 'B' },
    { number: 3, column: 'option_3', letter: 'C' },
    { number: 4, column: 'option_4', letter: 'D' }
]

const optionsOf = (question: QuizQuestion) => {
    return COLUMNS
        .map(option => ({ ...option, text: question[option.column] }))
        .filter(option => Boolean(option.text))
}

/* A question counts every answer as right when its correct option is 0 or points at an empty option. */
const isCorrect = (question: QuizQuestion, choice: number): boolean => {
    const options = optionsOf(question)
    const hasAnswer = options.some(option => option.number === question.correct_option)

    return !hasAnswer || question.correct_option === choice
}

const resultLine = (score: number, total: number): string => {
    if (score === total) return 'Perfect score! Tum mujhe mujhse bhi zyada jaanti ho.'
    if (score / total >= 0.6) return 'Bahut badhiya! Bas thoda aur time saath bitana padega.'

    return 'Koi baat nahi, seekhne ke liye poori zindagi padi hai.'
}

interface LoveQuizProps {
    section?: SectionRow
    questions: Array<QuizQuestion>
}

export default function LoveQuiz({ section, questions }: LoveQuizProps) {
    const [index, setIndex] = useState(0)
    const [choice, setChoice] = useState<number | null>(null)
    const [score, setScore] = useState(0)
    const [finished, setFinished] = useState(false)

    const total = questions.length
    const question = questions[Math.min(index, total - 1)]

    if (!question) return null

    const options = optionsOf(question)
    const answered = choice !== null
    const gotItRight = answered && isCorrect(question, choice)
    const last = index >= total - 1

    const answer = (number: number) => {
        if (answered) return

        setChoice(number)

        if (isCorrect(question, number)) setScore(value => value + 1)
    }

    const next = () => {
        if (last) {
            setFinished(true)
            if (score / total >= 0.6) void burstConfetti()

            return
        }

        setIndex(value => value + 1)
        setChoice(null)
    }

    const restart = () => {
        setIndex(0)
        setChoice(null)
        setScore(0)
        setFinished(false)
    }

    return (
        <div id='quiz' className='flex scroll-mt-8 flex-col rounded-[2.5rem] bg-white px-6 py-10 shadow-[0_30px_70px_-40px_rgba(91,21,48,0.5)] ring-1 ring-blush sm:px-9 sm:py-12'>
            <SectionHeading section={section} level='h3' size='md' />

            {finished
                ? (
                    <div className='mt-10 flex flex-1 animate-in flex-col items-center justify-center text-center duration-700 fade-in zoom-in-95'>
                        <span className='grid size-20 place-items-center rounded-full bg-gradient-to-br from-gold to-[#e8a94f] text-white shadow-lg'>
                            <Trophy aria-hidden className='size-9' />
                        </span>

                        <p className='mt-5 font-heading text-5xl font-semibold text-wine'>
                            {score} / {total}
                        </p>

                        <p className='mt-3 max-w-xs font-hand text-2xl leading-snug text-ink/85'>
                            {resultLine(score, total)}
                        </p>

                        <button
                            type='button'
                            onClick={restart}
                            className='mt-7 inline-flex items-center gap-2 rounded-full bg-wine px-5 py-2.5 text-sm font-bold text-cream transition outline-none hover:bg-petal-deep focus-visible:ring-4 focus-visible:ring-petal/40'
                        >
                            <RotateCcw aria-hidden className='size-4' />
                            Phir se khelo
                        </button>
                    </div>
                )
                : (
                    <div className='mt-9 flex flex-1 flex-col'>
                        <div className='flex items-center justify-between gap-4'>
                            <p className='text-xs font-bold tracking-[0.25em] text-petal-deep uppercase'>
                                Sawaal {index + 1} / {total}
                            </p>

                            <div aria-hidden className='flex gap-1.5'>
                                {questions.map((item, position) => (
                                    <span
                                        key={item.id}
                                        className={cn(
                                            'h-1.5 rounded-full transition-all duration-500',
                                            position === index ? 'w-6 bg-petal' : position < index ? 'w-1.5 bg-petal/60' : 'w-1.5 bg-blush'
                                        )}
                                    />
                                ))}
                            </div>
                        </div>

                        <div key={question.id} className='mt-5 flex flex-1 animate-in flex-col duration-500 fade-in slide-in-from-right-4'>
                            {question.question && (
                                <p
                                    data-mdlfy-cms={`quiz_questions:${question.id}:question`}
                                    className='font-heading text-2xl leading-snug font-semibold text-wine sm:text-[1.7rem]'
                                >
                                    {question.question}
                                </p>
                            )}

                            <div className='mt-6 grid gap-3 sm:grid-cols-2'>
                                {options.map(option => {
                                    const picked = choice === option.number
                                    const right = answered && isCorrect(question, option.number)
                                    const showRight = answered && right && (picked || !gotItRight)

                                    return (
                                        <button
                                            key={option.column}
                                            type='button'
                                            onClick={() => answer(option.number)}
                                            disabled={answered}
                                            aria-pressed={picked}
                                            className={cn(
                                                'flex items-center gap-3 rounded-2xl px-4 py-3.5 text-left text-base font-semibold ring-1 transition outline-none focus-visible:ring-4 focus-visible:ring-petal/40 disabled:cursor-default',
                                                !answered && 'bg-cream text-ink ring-blush hover:-translate-y-0.5 hover:bg-blush hover:ring-petal/40',
                                                answered && !picked && !showRight && 'bg-cream/60 text-ink/50 ring-blush',
                                                showRight && 'bg-[#e9f7ee] text-[#1f6b3d] ring-[#7cc79a]',
                                                answered && picked && !right && 'bg-[#fdecef] text-petal-deep ring-petal/50'
                                            )}
                                        >
                                            <span className={cn(
                                                'grid size-8 shrink-0 place-items-center rounded-full text-sm font-bold',
                                                showRight ? 'bg-[#2f9b5c] text-white' : answered && picked ? 'bg-petal-deep text-white' : 'bg-white text-petal-deep ring-1 ring-blush'
                                            )}>
                                                {showRight && <CircleCheck aria-hidden className='size-4' />}
                                                {answered && picked && !right && <CircleX aria-hidden className='size-4' />}
                                                {!showRight && !(answered && picked && !right) && option.letter}
                                            </span>

                                            <span data-mdlfy-cms={`quiz_questions:${question.id}:${option.column}`}>
                                                {option.text}
                                            </span>
                                        </button>
                                    )
                                })}
                            </div>

                            <div className='mt-6 min-h-24' aria-live='polite'>
                                {answered && (
                                    <div className='flex animate-in flex-col gap-4 duration-500 fade-in slide-in-from-bottom-2 sm:flex-row sm:items-center sm:justify-between'>
                                        <div className='flex items-start gap-2'>
                                            {gotItRight
                                                ? <CircleCheck aria-hidden className='mt-1 size-5 shrink-0 text-[#2f9b5c]' />
                                                : <CircleX aria-hidden className='mt-1 size-5 shrink-0 text-petal-deep' />}

                                            {question.reaction
                                                ? (
                                                    <p
                                                        data-mdlfy-cms={`quiz_questions:${question.id}:reaction`}
                                                        className='font-hand text-2xl leading-snug text-ink'
                                                    >
                                                        {question.reaction}
                                                    </p>
                                                )
                                                : (
                                                    <p className='font-hand text-2xl leading-snug text-ink'>
                                                        {gotItRight ? 'Sahi jawab!' : 'Oops, galat jawab.'}
                                                    </p>
                                                )}
                                        </div>

                                        <button
                                            type='button'
                                            onClick={next}
                                            className='inline-flex shrink-0 items-center justify-center gap-2 rounded-full bg-gradient-to-r from-petal to-petal-deep px-5 py-2.5 text-sm font-bold text-white shadow transition outline-none hover:brightness-110 focus-visible:ring-4 focus-visible:ring-petal/40'
                                        >
                                            {last ? 'Result dekho' : 'Agla sawaal'}
                                            <ArrowRight aria-hidden className='size-4' />
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
        </div>
    )
}
